import React, { useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * Componente de feedback unificado para éxito, error, confirmación e información
 * - Usa estilos globales definidos en components.css (feedback-dialog-*)
 * - Alineado con variables CSS y UI general
 */
const typeToDefaults = {
  success: {
    title: 'Operación completada',
    Icon: CheckCircleOutlineIcon,
    confirmText: 'Entendido',
  },
  error: {
    title: 'Ha ocurrido un error',
    Icon: ErrorOutlineIcon,
    confirmText: 'Cerrar',
  },
  confirm: {
    title: '¿Confirmas esta acción?',
    Icon: HelpOutlineIcon,
    confirmText: 'Confirmar',
    cancelText: 'Cancelar',
  },
  info: {
    title: 'Información',
    Icon: InfoOutlinedIcon,
    confirmText: 'Aceptar',
  },
};

const FeedbackDialog = ({
  open,
  type = 'success',
  title,
  message,
  onConfirm,
  onClose,
  confirmText,
  cancelText,
  showCancel = false,
  autoCloseMs,
  disableBackdropClose = false,
}) => {
  const defaults = typeToDefaults[type] || typeToDefaults.success;
  const TitleIcon = defaults.Icon;

  useEffect(() => {
    if (!open) return;
    if (!autoCloseMs || type === 'confirm') return;
    const timer = setTimeout(() => {
      try {
        onClose && onClose();
      } catch (error) {
        console.error('Error cerrando FeedbackDialog automáticamente:', error);
      }
    }, autoCloseMs);
    return () => clearTimeout(timer);
  }, [open, autoCloseMs, type, onClose]);

  const handleClose = (_, reason) => {
    if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }
    try {
      onClose && onClose();
    } catch (error) {
      console.error('Error al cerrar FeedbackDialog:', error);
    }
  };

  const handleConfirm = () => {
    try {
      if (onConfirm) onConfirm();
      else if (onClose) onClose();
    } catch (error) {
      console.error('Error en confirmación de FeedbackDialog:', error);
    }
  };

  const resolvedTitle = title || defaults.title;
  const resolvedConfirm = confirmText || defaults.confirmText;
  const resolvedCancel = cancelText || defaults.cancelText || 'Cancelar';
  const shouldShowCancel = showCancel || type === 'confirm';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="feedback-dialog-title"
      aria-describedby="feedback-dialog-description"
      PaperProps={{
        className: `feedback-dialog-paper feedback-dialog-${type}`,
      }}
    >
      <div className={`feedback-dialog-header feedback-dialog-header-${type}`}>
        <Box className="feedback-dialog-icon-wrapper">
          <TitleIcon className={`feedback-dialog-icon feedback-dialog-icon-${type}`} />
        </Box>
        <Typography id="feedback-dialog-title" className="feedback-dialog-title">
          {resolvedTitle}
        </Typography>
      </div>

      <DialogContent className="feedback-dialog-content">
        {typeof message === 'string' ? (
          <Typography id="feedback-dialog-description" className="feedback-dialog-message">
            {message}
          </Typography>
        ) : (
          message
        )}
      </DialogContent>

      <DialogActions className="feedback-dialog-actions">
        {shouldShowCancel && (
          <Button onClick={handleClose} className="btn btn-outline">
            {resolvedCancel}
          </Button>
        )}
        <Button onClick={handleConfirm} autoFocus className="btn btn-primary">
          {resolvedConfirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;


